import assert from '../assert'
import cachedFunction from '../cachedFunction'
import equals from './equals'
import filter from './filter'
import findInTree from './findInTree'
import flatMap from './flatMap'
import indexTree from './indexTree'
import insert from './insert'
import remove from './remove'
import unwrap from './unwrap'

const reorder = cachedFunction(
  function (inputTree, { sourceIds, targetId, targetShift = 0, level = 0 }, validate) {
    const sources = sourceIds.map((id) => findInTree(inputTree, (n) => n.get('id') === id))

    assert('Must at least provide one source id', sources.length > 0)
    assert(
      'Sources must all have the same parent',
      sources.every((n) => n.get('__parentId') === sources[0].get('__parentId')),
    )
    assert(
      'Sources must be in order and without gaps',
      sources.reduce(
        ({ lastIndex }, n) => {
          const currIndex = n.get('__indexLocal')
          return lastIndex
            ? { ok: currIndex === lastIndex + 1, lastIndex: currIndex }
            : { ok: true, lastIndex: currIndex }
        },
        { ok: true },
      ).ok,
    )

    // remove source items and recalculate the indices
    // from this point an the reordering is handled like an insertion
    // as additional information we know if the source item is moved downwards or upwards
    const withoutSources = remove(inputTree, sources)
    const reindexedTree = indexTree(withoutSources)

    // if moving items with subitems, we have to limit the target index
    const targetIndex = Math.min(
      findInTree(inputTree, (n) => n.get('id') === targetId).get('__indexGlobal') + targetShift,
      inputTree.get(0).get('__totalChildrenCount') -
        sources.reduce((sum, n) => sum + n.get('__totalChildrenCount'), 0) -
        sources.length +
        1,
    )

    // find possible parents that could hold the reinserted source item
    const phantomTarget = findInTree(reindexedTree, (n) => n.get('__indexGlobal') === targetIndex)
    const parentsTrace = flatMap(
      filter(
        reindexedTree,
        (n) =>
          targetIndex > n.get('__indexGlobal') &&
          targetIndex - 1 <= n.get('__indexGlobal') + n.get('__totalChildrenCount'),
      ),
      (n) => n,
    )

    // define function that generates the new tree
    const generateNewTreeFromPotentialParent = (parentId) => {
      // find parent in reindexed tree
      const parent = findInTree(reindexedTree, (n) => n.get('id') === parentId)

      // find values needed to do the actual insertion
      const parentChild = findInTree(parent.get('children'), (c) => c.get('__indexGlobal') === targetIndex)
      const parentIndexLocal = parentChild ? parentChild.get('__indexLocal') : parent.get('children').count()

      // reinsert source item into new position
      return insert(reindexedTree, sources, parent, parentIndexLocal)
    }

    // cross out the parents, that cannot hold our source item, since else we would brake the given nesting
    // i.e. we would not just change the parent of the source item, but also the parent of other items
    const potentialParents = parentsTrace.filter((n) => {
      const phantomTargetLevel = phantomTarget ? phantomTarget.get('__level') : '*'
      const isImmediateSuccessor = targetIndex - 1 === n.get('__indexGlobal') + n.get('__totalChildrenCount')
      const isNotASplitUp = phantomTargetLevel !== '*' ? phantomTargetLevel - 1 === n.get('__level') : true

      return isImmediateSuccessor || isNotASplitUp
    })

    // find out parents that would make a new tree that is forbidden by user validation
    const validations = {}
    if (validate) {
      potentialParents.forEach((n) => {
        const oldData = indexTree(unwrap(inputTree))
        const newData = indexTree(unwrap(generateNewTreeFromPotentialParent(n.get('id'))))
        validations[n.get('id')] = validate(newData, oldData)
      })
    }

    // return original tree it there is not potential parent left
    if (potentialParents.count() === 0) return inputTree

    // find the nearest possible parent that matches the wanted item level
    const parentId = (potentialParents.filter((p) => p.get('__level') <= level).last() || potentialParents.first()).get(
      'id',
    )
    const newTree = generateNewTreeFromPotentialParent(parentId)

    return {
      validationError: validations[parentId] !== true && validations[parentId],
      // check if this movement did actually change anything and return either the new tree or the old one
      newTree: !equals(inputTree, newTree) ? indexTree(newTree) : inputTree,
    }
  },
  function (args, lastArgs) {
    return (
      args[0] === lastArgs[0] &&
      args[1].sourceIds === lastArgs[1].sourceIds &&
      args[1].targetId === lastArgs[1].targetId &&
      args[1].targetShift === lastArgs[1].targetShift &&
      args[1].level === lastArgs[1].level
    )
  },
)

export default reorder
